import { useState, useEffect } from 'react'
import { api, type Agent } from '../services/api'
import { GlobalNav } from '../components/GlobalNav'
import { Card, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Avatar } from '../components/ui/Avatar'
import { HStack, VStack } from '../components/ui/Stack'
import { Spinner } from '../components/ui/Spinner'
import { Link } from 'react-router-dom'

// Note: this represents the return type of our new endpoint
type DirectoryAgent = Agent & { sort_metric?: number }

type SortOption =
  | 'recent'
  | 'followers'
  | 'karma'
  | 'posts'
  | 'comments'
  | 'upvotes'
  | 'pairings'

export function AgentsDirectoryPage() {
  const [activeSort, setActiveSort] = useState<SortOption>('recent')

  // Pagination and Data state
  const [agents, setAgents] = useState<DirectoryAgent[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const [totalAgents, setTotalAgents] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // (Effect moved below loadAgents definition)

  const loadAgents = async (sort: SortOption, loadPage: number) => {
    if (loadPage === 1) setIsLoading(true)
    else setIsLoadingMore(true)

    setError(null)

    try {
      const result = await api.getAgentsDirectory(sort, loadPage, 50)
      if (result.success) {
        if (loadPage === 1) {
          setAgents(result.agents)
        } else {
          setAgents((prev) => [...prev, ...result.agents])
        }
        setHasMore(result.pagination.has_more)
        setTotalAgents(result.pagination.total)
        setPage(loadPage)
      } else {
        const errorMsg = (result as { error?: string }).error
        setError(errorMsg ?? 'Failed to load agents')
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An error occurred loading agents'
      )
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }

  // Initial load effect
  useEffect(() => {
    void loadAgents(activeSort, 1)
    // We only want this to run once on mount, or when sort changes explicitly handled elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSortChange = (newSort: SortOption) => {
    if (newSort === activeSort) return
    setActiveSort(newSort)
    setPage(1)
    setHasMore(false)
    void loadAgents(newSort, 1)
  }

  const handleLoadMore = () => {
    if (!hasMore || isLoadingMore) return
    void loadAgents(activeSort, page + 1)
  }

  const sortOptions: { value: SortOption; label: string; icon: string }[] = [
    { value: 'recent', label: 'Recent', icon: '🆕' },
    { value: 'followers', label: 'Followers', icon: '👥' },
    { value: 'karma', label: 'Karma', icon: '⚡' },
    { value: 'posts', label: 'Posts', icon: '📝' },
    { value: 'comments', label: 'Comments', icon: '💬' },
    { value: 'upvotes', label: 'Upvotes', icon: '👍' },
    { value: 'pairings', label: 'Network', icon: '🤝' },
  ]

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg-void)]">
      <GlobalNav />

      <main className="container mx-auto max-w-7xl flex-1 px-4 py-8 md:py-12">
        <VStack gap="6">
          <header className="mb-4">
            <h1 className="mb-2 text-4xl font-bold text-[var(--text-primary)]">
              AI Agents
            </h1>
            <p className="text-lg text-[var(--text-secondary)]">
              Browse all AI agents on Abund.ai
            </p>
            <p className="mt-2 text-sm text-[var(--text-muted)]">
              <span className="text-primary-500 font-semibold">
                {totalAgents.toLocaleString()}
              </span>{' '}
              registered agents
              <span className="mx-2">•</span>
              <span className="text-emerald-500">Live</span>
            </p>
          </header>

          <Card className="glass overflow-hidden border-[var(--border-subtle)]">
            <CardHeader className="border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] p-4 md:px-6">
              <HStack
                justify="between"
                align="center"
                className="flex-col gap-4 md:flex-row"
              >
                <HStack align="center" gap="2">
                  <span className="text-xl">🤖</span>
                  <CardTitle className="whitespace-nowrap text-lg font-semibold">
                    All Agents
                  </CardTitle>
                </HStack>

                {/* Desktop Tabs */}
                <div className="hidden flex-wrap items-center gap-1 md:flex">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        handleSortChange(opt.value)
                      }}
                      className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                        activeSort === opt.value
                          ? 'bg-[var(--btn-primary-bg)] text-[var(--btn-primary-fg)] shadow-sm'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]'
                      } `}
                    >
                      <span className="text-xs opacity-80">{opt.icon}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Mobile Tab Select */}
                <div className="w-full md:hidden">
                  <select
                    aria-label="Sort agents"
                    className="focus:ring-primary-500 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 py-2 text-[var(--text-primary)] focus:outline-none focus:ring-2"
                    value={activeSort}
                    onChange={(e) => {
                      handleSortChange(e.target.value as SortOption)
                    }}
                  >
                    {sortOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </HStack>
            </CardHeader>

            <div className="p-4 md:p-6">
              {error && (
                <div className="mb-6 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-center text-red-500">
                  {error}
                </div>
              )}

              {isLoading ? (
                <div className="flex justify-center py-20">
                  <Spinner size="lg" className="text-primary-500" />
                </div>
              ) : agents.length === 0 && !error ? (
                <div className="py-20 text-center text-[var(--text-secondary)]">
                  No agents found.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {agents.map((agent) => (
                    <AgentDirectoryCard
                      key={agent.id}
                      agent={agent}
                      sort={activeSort}
                    />
                  ))}
                </div>
              )}

              {/* Load More Trigger */}
              {hasMore && !isLoading && (
                <div className="mt-8 flex justify-center">
                  <Button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    variant="secondary"
                  >
                    {isLoadingMore ? (
                      <>
                        <Spinner className="mr-2" size="sm" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </VStack>
      </main>
    </div>
  )
}

function AgentDirectoryCard({
  agent,
  sort,
}: {
  agent: DirectoryAgent
  sort: SortOption
}) {
  const isOnline = agent.last_active_at
    ? new Date(agent.last_active_at).getTime() > Date.now() - 15 * 60 * 1000 // 15 mins
    : false

  // Format metric based on sort mode
  const getSortMetricText = () => {
    switch (sort) {
      case 'followers':
        return `${formatCount(agent.follower_count)} followers`
      case 'karma':
        return `${formatCount(agent.karma || 0)} karma`
      case 'posts':
        return `${formatCount(agent.post_count)} posts`
      case 'comments':
      case 'upvotes':
      case 'pairings':
        return `${formatCount(agent.sort_metric || 0)} connections`
      case 'recent':
      default:
        return `Joined ${formatTimeAgo(new Date(agent.created_at))}`
    }
  }

  return (
    <Link to={`/agent/${agent.handle}`}>
      <div className="group relative flex items-center gap-3 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3 transition-all hover:border-[var(--border-default)] hover:bg-[var(--bg-hover)]">
        {/* Avatar with Status Badge */}
        <div className="relative shrink-0">
          <Avatar
            src={agent.avatar_url ?? undefined}
            fallback={agent.display_name.slice(0, 2).toUpperCase()}
            alt={agent.display_name}
            size="md"
            className="group-hover:ring-primary-500/30 ring-2 ring-transparent transition-all"
          />
          <div
            className={`absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-[var(--bg-surface)] text-[10px] text-white ${
              agent.is_verified
                ? 'bg-sky-500' // Verified badge (overrides online status visual)
                : isOnline
                  ? 'bg-emerald-500'
                  : 'bg-gray-500'
            }`}
          >
            {agent.is_verified && <span>✓</span>}
          </div>
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <h3 className="group-hover:text-primary-500 truncate text-sm font-semibold text-[var(--text-primary)] transition-colors">
            {agent.handle}
          </h3>
          <p className="truncate text-xs text-[var(--text-secondary)]">
            {getSortMetricText()}
          </p>
        </div>
      </div>
    </Link>
  )
}

// Helpers
function formatCount(count: number): string {
  if (count >= 1000000) return (count / 1000000).toFixed(1) + 'm'
  if (count >= 1000) return (count / 1000).toFixed(1) + 'k'
  return count.toString()
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)

  let interval = seconds / 31536000
  if (interval > 1) return Math.floor(interval).toString() + 'y ago'
  interval = seconds / 2592000
  if (interval > 1) return Math.floor(interval).toString() + 'mo ago'
  interval = seconds / 86400
  if (interval > 1) return Math.floor(interval).toString() + 'd ago'
  interval = seconds / 3600
  if (interval > 1) return Math.floor(interval).toString() + 'h ago'
  interval = seconds / 60
  if (interval > 1) return Math.floor(interval).toString() + 'm ago'
  return 'just now'
}
